import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { Link } from 'react-router';
import type { AppRouterOutputs } from '@framework/trpcClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faProjectDiagram, faUser } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@framework/ui/atoms/Tooltip';
import { Timestamp } from '@framework/Timestamp';
import { buildDateTimeArgs } from '@framework/i18n/buildDateTimeArgs';
import { ViewModelIndividualPermissionDeleteConfirmModal } from './ViewModelIndividualPermissionDeleteConfirmModal';
import { useState } from 'react';
import type { GroupMember } from '@group/hooks/useGroupMembers';
import { useTranslation } from 'react-i18next';

type AssignedViewModel =
  AppRouterOutputs['viewModel']['individualMember']['listIndividualPermittedViewModels']['results'][0];

type Props = {
  workspace: AssignedViewModel['workspace'];
  viewModel: AssignedViewModel['viewModel'];
  memberUserEntity: GroupMember;
  onChanged(): void;
};

export const ViewModelItem: React.FC<Props> = ({ workspace, viewModel, memberUserEntity, onChanged }: Props) => {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const publicReadable =
    workspace.setting.isPublicSpace ||
    (workspace.setting.isViewModelURLShareable && viewModel.sharingUserRole !== 'None');
  const individualPermissionConfigured = workspace.setting.enableIndividualViewModelPermissions;

  return (
    <div className="mb-2 flex h-12 w-full items-center rounded border border-gray-500 bg-white hover:bg-gray-200">
      <div className="relative flex h-full w-16 items-center justify-center rounded-l bg-brand text-center text-white">
        <span className="text-2xl">
          <FontAwesomeIcon icon={faProjectDiagram} />
        </span>
        {publicReadable ? (
          <span className="absolute bottom-0 right-0 inline-block size-4 -translate-x-2 -translate-y-3 text-xl">
            <Tooltip tooltip={t('group.viewModelItem.tooltipPublic')} placement="bottom">
              <FontAwesomeIcon icon={faGlobe} />
            </Tooltip>
          </span>
        ) : individualPermissionConfigured ? (
          <span className="absolute bottom-0 right-0 inline-block size-4 -translate-x-2 -translate-y-3 text-xl">
            <Tooltip tooltip={t('group.viewModelItem.tooltipPermission')} placement="bottom">
              <FontAwesomeIcon icon={faUser} />
            </Tooltip>
          </span>
        ) : null}
      </div>
      <Link
        className="flex-1 truncate px-2 text-xl font-bold hover:text-blue-800"
        to={UserPagePathBuilder.viewModelPage(viewModel.id)}
        target="_blank"
        rel="noreferrer"
      >
        {viewModel.name}
        <div className="flex gap-x-2 text-3xs text-gray-500">
          {viewModel.updatedAt && (
            <span>
              {t('common.label.lastUpdatedAt', {
                dateTime: t(
                  'common.dateTimeFormat',
                  buildDateTimeArgs(Timestamp.fromUnixTimestamp(viewModel.updatedAt))
                ),
              })}
            </span>
          )}
          <span>{workspace.name}</span>
        </div>
      </Link>

      <button
        className={'mr-2 border border-red-700 p-1 text-sm text-red-700 hover:bg-red-100 focus:outline-none'}
        onClick={() => setDeleting(true)}
      >
        {t('group.viewModelItem.deletePermission')}
      </button>

      {deleting && (
        <ViewModelIndividualPermissionDeleteConfirmModal
          isOpen
          onClose={() => setDeleting(false)}
          memberData={memberUserEntity}
          viewModelId={viewModel.id}
          viewModelName={viewModel.name}
          onDeleted={onChanged}
        />
      )}
    </div>
  );
};
